import { merge } from 'lodash';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import {
  ApiFilterOperation,
  EvidenceProps,
  Filter,
  abortableFetch,
  getItemFactory,
  updateItemFactory,
} from '@eas/common-web';
import { SaveButton } from '@composite/settlement-method/save-button';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { isCourtFileDetailLocked } from '@components/evidence/utils/is-locked';
import { CourtFile } from '@models';
import { Agenda, CourtFileType, EsFileState, EvidenceApiUrl } from '@enums';
import { disableBtnFactory } from '../es-file/es-file-utils';
import { useEsFiles } from '../es-file/es-files';

export const COURT_FILES_IDENTIFIER = 'COURT_FILES';
export const COURT_FILES_VERSION = 10;

export function useCourtFiles({
  evidenceProps,
}: {
  evidenceProps?: Partial<EvidenceProps<CourtFile>>;
}) {
  const intl = useIntl();

  const { defaultCourtProcessingType, defaultSettlementMethods } =
    useContext(StaticDataContext);
  const defaultSettlementMethod = defaultSettlementMethods.find(
    (method) => method.agenda === Agenda.COURT
  );

  const defaultPreFilters = [
    ...((evidenceProps?.tableProps?.defaultPreFilters ?? []) as Filter[]),
    {
      field: 'agenda.id',
      operation: ApiFilterOperation.EQ,
      value: Agenda.COURT,
    },
  ];

  const defaultCourtFile = {
    id: uuidv4(),
    created: new Date().toISOString(),
    agenda: Agenda.COURT,
    state: EsFileState.NEW,
    type: CourtFileType.SENATE,
    processingType: defaultCourtProcessingType!,
    settlementMethods: defaultSettlementMethod ? [defaultSettlementMethod] : [],
  };

  const props = merge(
    {
      evidenceProps: {
        identifier: COURT_FILES_IDENTIFIER,
        version: COURT_FILES_VERSION,
        apiProps: {
          getItem: getItemFactory({ postProcess: postprocessGetCall }),
          updateItem: updateItemFactory({
            updateItemMethod: updateCourtFile,
            postProcess: postprocessGetCall,
          }),
        },
        tableProps: {
          tableName: intl.formatMessage({
            id: 'ES__COURT_FILES__TABLE__TITLE',
            defaultMessage: 'Soudní spisy',
          }),
          defaultPreFilters,
        },
        detailProps: {
          lock: isCourtFileDetailLocked,
          toolbarProps: {
            title: intl.formatMessage({
              id: 'ES__COURT_FILES__DETAIL__TITLE',
              defaultMessage: 'Soudní spis',
            }),
            disableBtn: disableBtnFactory(defaultCourtFile),
            SaveButton,
          },
          initNewItem: () => defaultCourtFile,
        },
      },
    },
    { evidenceProps }
  );

  return useEsFiles(props);
}

const postprocessGetCall = async (esFile: CourtFile) => {
  const challengedActResponse = await fetch(
    `${EvidenceApiUrl.ES_FILES}/${esFile.id}/challenged-act/list`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        size: -1,
      }),
    }
  );

  const challengedActs = await challengedActResponse.json();

  return {
    ...esFile,
    challengedActs: challengedActs.items ?? [],
  };
};

function updateCourtFile(api: string, item: CourtFile, initialItem: CourtFile) {
  if (initialItem.state !== EsFileState.CLOSED) {
    const { changeReason, ...updatedItem } = item as any;
    const changeReasonParam = changeReason
      ? `?changeReason=${encodeURIComponent(changeReason)}`
      : '';
    return abortableFetch(`${api}/${item.id}${changeReasonParam}`, {
      headers: new Headers({ 'Content-Type': 'application/json' }),
      method: 'PUT',
      body: JSON.stringify(updatedItem),
    });
  }

  return abortableFetch(`${api}/${item.id}/update-closed`, {
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    method: 'PUT',
    body: JSON.stringify({
      version: item.version,
      processingType: item.processingType,
      settlementMethods: item.settlementMethods,
      proceedingsSubjects: item.proceedingsSubjects,
      subjectIndexes: item.subjectIndexes,
      analystUserRoleId: item.analystUserRole
        ? { id: item.analystUserRole.id }
        : undefined,
    }),
  });
}
